import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import useScanCounters from '../../hooks/useScanCounters';
import useScanCountersStore from '../../stores/scanCountersStore';
import { COUNTER_TYPES, MAX_COUNTERS } from '../../services/api/scanCountersApi';
import { typeBadgeClass } from '../../utils/scanCounters';
import ConfirmationModal from '../common/ConfirmationModal';
import ScanCounterPrintCard from '../abha/counters/ScanCounterPrintCard';
import { SETTINGS_COPY } from '../../constants/abdmCopy';

const PAGE = 10;
const ID_RE = /^[A-Za-z0-9_-]{1,10}$/;
const EMPTY = { counterId: '', name: '', type: COUNTER_TYPES.OPD };

const inputCls = 'px-2.5 py-2 rounded-lg border border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-60';
const btnPrimary = 'px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnGhost = 'px-3 py-2 rounded-lg border border-slate-400 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60';
const btnSm = 'px-2.5 py-1 rounded-md border text-xs font-semibold bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700';

/**
 * Scan & Share counters (SSC) - admin card on the Settings page, right under the HIP ID.
 * The counter numbers must match the ones created on the ABDM facility portal. The QR text is the
 * server's `qr_text`; the FE never builds the share URL.
 */
export default function ScanShareCountersSettings() {
  const { hipId, counters, loaded, failed, retry } = useScanCounters(true);
  const saveCounters = useScanCountersStore((s) => s.saveCounters);

  const [form, setForm] = useState(null);        // null = closed; {counterId, name, type, editing}
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [limit, setLimit] = useState(PAGE);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return counters.filter((c) => (typeFilter === 'ALL' || c.type === typeFilter)
      && (!needle || c.counterId.toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle)));
  }, [counters, q, typeFilter]);
  const shown = filtered.slice(0, limit);

  const validate = () => {
    const id = form.counterId.trim();
    const name = form.name.trim();
    if (!ID_RE.test(id)) return 'Counter no. must be 1-10 letters, numbers, "-" or "_".';
    if (!name || name.length > 40) return 'Name must be 1-40 characters.';
    if (!form.editing && counters.some((c) => c.counterId === id)) return `Counter ${id} already exists.`;
    if (!form.editing && counters.length >= MAX_COUNTERS) return `You can add up to ${MAX_COUNTERS} counters.`;
    return '';
  };

  const run = async (next, after) => {
    setBusy(true);
    setErr('');
    try {
      await saveCounters(next);
      after();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onSave = () => {
    const problem = validate();
    if (problem) { setErr(problem); return; }
    const entry = { counterId: form.counterId.trim(), name: form.name.trim(), type: form.type };
    const next = form.editing
      ? counters.map((c) => (c.counterId === entry.counterId ? entry : c))
      : [...counters, entry];
    run(next, () => setForm(null));
  };

  const onDelete = () => {
    const id = toDelete.counterId;
    setToDelete(null);
    run(counters.filter((c) => c.counterId !== id), () => {});
  };

  const hipSet = Boolean(hipId);

  return (
    <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 mt-5 shadow-sm" data-testid="ssc-settings">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">Scan &amp; Share counters</h3>
          {loaded && hipSet && (
            <div className="text-xs text-slate-600 dark:text-slate-300">
              HIP ID: {hipId} · {counters.length} counter{counters.length === 1 ? '' : 's'}
              {counters.length > PAGE && ` · showing ${Math.min(limit, filtered.length)}`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {loaded && counters.length > PAGE && (
            <>
              <input aria-label="Search counters" placeholder="Search by number or name" value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} className={`${inputCls} w-56`} data-testid="ssc-search" />
              <select aria-label="Filter by type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setLimit(PAGE); }} className={inputCls} data-testid="ssc-type-filter">
                <option value="ALL">All types</option>
                <option value="OPD">OPD</option>
                <option value="IPD">IPD</option>
              </select>
            </>
          )}
          <button type="button" data-testid="ssc-add" disabled={!loaded || !hipSet || busy} onClick={() => { setErr(''); setForm({ ...EMPTY }); }} className={btnPrimary}>
            + Add counter
          </button>
        </div>
      </div>

      {failed && (
        <div className="mt-3 text-sm text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-amber-900/40 border border-amber-500 rounded-lg p-3" data-testid="ssc-load-error">
          Could not load the counters. <button type="button" onClick={retry} className="underline font-semibold">Try again</button>
        </div>
      )}

      {loaded && !hipSet && (
        <div className="mt-3 text-sm text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-amber-900/40 border border-amber-500 rounded-lg p-3" data-testid="ssc-no-hip">
          <b>{SETTINGS_COPY.noHipBold}</b> {SETTINGS_COPY.noHipRest}
        </div>
      )}

      {form && (
        <div className="mt-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-3" data-testid="ssc-form">
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <label htmlFor="ssc-id" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Counter no.</label>
              <input id="ssc-id" data-testid="ssc-form-id" className={`${inputCls} w-24`} value={form.counterId} disabled={busy || form.editing} onChange={(e) => setForm({ ...form, counterId: e.target.value })} />
            </div>
            <div>
              <label htmlFor="ssc-name" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Name</label>
              <input id="ssc-name" data-testid="ssc-form-name" className={`${inputCls} w-52`} value={form.name} disabled={busy} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label htmlFor="ssc-type" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Type</label>
              <select id="ssc-type" data-testid="ssc-form-type" className={inputCls} value={form.type} disabled={busy} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="OPD">OPD</option>
                <option value="IPD">IPD</option>
              </select>
            </div>
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 mt-2">Use the same counter numbers you created on the ABDM facility portal.</div>
          {err && <div className="text-xs text-rose-700 dark:text-rose-300 mt-2" data-testid="ssc-error">{err}</div>}
          <div className="flex gap-2 mt-3">
            <button type="button" data-testid="ssc-save" onClick={onSave} disabled={busy} className={btnPrimary}>{busy ? 'Saving…' : 'Save counter'}</button>
            <button type="button" data-testid="ssc-cancel" onClick={() => { setForm(null); setErr(''); }} disabled={busy} className={btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      {loaded && hipSet && counters.length === 0 && !form && (
        <div className="mt-3 rounded-xl border-2 border-dashed border-slate-400 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-6 text-center text-slate-700 dark:text-slate-200" data-testid="ssc-empty">
          <b>No counters yet</b>
          <div className="text-sm mt-1">Add the counters you created on the ABDM facility portal, so each desk gets its own QR.</div>
          <button type="button" data-testid="ssc-add-first" onClick={() => { setErr(''); setForm({ ...EMPTY }); }} className={`${btnPrimary} mt-3`}>+ Add your first counter</button>
        </div>
      )}

      {err && !form && <div className="text-xs text-rose-700 dark:text-rose-300 mt-2" data-testid="ssc-error">{err}</div>}

      {counters.length > 0 && (
        <>
          <table className="w-full mt-3 text-sm" data-testid="ssc-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900">
                <th className="p-2 border-b border-slate-300 dark:border-slate-600">Counter no.</th>
                <th className="p-2 border-b border-slate-300 dark:border-slate-600">Name</th>
                <th className="p-2 border-b border-slate-300 dark:border-slate-600">Type</th>
                <th className="p-2 border-b border-slate-300 dark:border-slate-600">QR</th>
                <th className="p-2 border-b border-slate-300 dark:border-slate-600" />
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.counterId} data-testid="ssc-row" className="text-slate-900 dark:text-slate-100">
                  <td className="p-2 border-b border-slate-300 dark:border-slate-600 font-bold">{c.counterId}</td>
                  <td className="p-2 border-b border-slate-300 dark:border-slate-600">{c.name}</td>
                  <td className="p-2 border-b border-slate-300 dark:border-slate-600">
                    <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${typeBadgeClass(c.type)}`}>{c.type}</span>
                  </td>
                  <td className="p-2 border-b border-slate-300 dark:border-slate-600">
                    {c.qrText ? (
                      <span className="inline-block bg-white p-0.5 border border-slate-300 rounded leading-[0]"><QRCodeSVG value={c.qrText} size={48} level="M" /></span>
                    ) : <span className="text-xs text-slate-500 dark:text-slate-400">—</span>}
                  </td>
                  <td className="p-2 border-b border-slate-300 dark:border-slate-600 whitespace-nowrap">
                    {c.qrText && <button type="button" data-testid="ssc-print" onClick={() => setPrinting(c)} className={`${btnSm} border-teal-600 dark:border-teal-500 text-teal-800 dark:text-teal-200`}>Print</button>}{' '}
                    <button type="button" data-testid="ssc-edit" disabled={busy} onClick={() => { setErr(''); setForm({ ...c, editing: true }); }} className={`${btnSm} border-slate-400 dark:border-slate-600 text-slate-800 dark:text-slate-100`}>Edit</button>{' '}
                    <button type="button" data-testid="ssc-delete" disabled={busy} onClick={() => setToDelete(c)} className={`${btnSm} border-red-500 text-red-700 dark:text-red-300`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-sm text-slate-600 dark:text-slate-300 mt-2" data-testid="ssc-no-match">No counters match your search.</p>}
          {filtered.length > limit && (
            <div className="text-center mt-2">
              <button type="button" data-testid="ssc-more" onClick={() => setLimit(limit + PAGE)} className={btnGhost}>Show {filtered.length - limit} more</button>
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={Boolean(toDelete)}
        title={toDelete ? `Delete counter ${toDelete.counterId} - ${toDelete.name}?` : ''}
        message={toDelete ? `Its QR will stop showing in the app. Patients who scan a printed copy will still appear in the waiting list, as "Counter ${toDelete.counterId}".` : ''}
        ctaLabel="Delete counter"
        cancelLabel="Keep counter"
        variant="danger"
        icon="🗑️"
        autoFocusCancel
        onConfirm={onDelete}
        onCancel={() => setToDelete(null)}
      />
      {printing && <ScanCounterPrintCard counter={printing} onClose={() => setPrinting(null)} />}
    </div>
  );
}
